import type { LucideIcon } from "lucide-react";
import {
  AudioLinesIcon,
  BicepsFlexedIcon,
  BookmarkIcon,
  BotIcon,
  CalendarCheckIcon,
  FlameIcon,
  FolderKanbanIcon,
  GlobeIcon,
  LayersIcon,
  LibraryIcon,
  MedalIcon,
  MessageCircleIcon,
  MessagesSquareIcon,
  RocketIcon,
  ShuffleIcon,
  SparklesIcon,
  TelescopeIcon,
  TerminalIcon,
  WorkflowIcon,
  WrenchIcon,
} from "lucide-react";

// ── Types (mirror the SQL payloads in 20260827000003_add_gamification) ──────

export type GamificationStats = {
  total_chat_messages: number;
  total_project_messages: number;
  total_messages: number;
  first_message_at: string | null;
  agents_created: number;
  meetings_uploaded: number;
  artifacts_created: number;
  prompts_saved: number;
  workflow_sessions: number;
  distinct_models_used: number;
  used_web_search: boolean;
  used_code_execution: boolean;
  used_knowledge_base: boolean;
  lifetime_active_days: number;
  monthly_active_days: number;
  monthly_messages: number;
  max_active_days_in_week: number;
  best_week_streak: number;
  current_week_streak: number;
  lifetime_categories_used: number;
  lifetime_score: number;
};

export type MonthlyScoreRow = {
  month_start: string;
  score: number;
  breadth_points: number;
  consistency_points: number;
  volume_points: number;
  active_days: number;
  categories_used: number;
  rank: number | null;
  percentile_band: number | null;
  computed_at: string;
};

export type SnapshotPayload = {
  month_start: string;
  generated_at: string;
  top: { rank: number; display_name: string; score: number }[];
  most_improved: { display_name: string; delta: number }[];
  collective: {
    total_conversations: number;
    active_members_month: number;
    messages_month: number;
  };
};

// ── Levels ───────────────────────────────────────────────────────────────────
// The lifetime score these thresholds map is monotonic (high-water mark in
// user_gamification), so a level can never go down. The nightly refresh stamps
// the same thresholds onto profiles.gamify for admin reporting — if you tune
// them here, update the CASE in refresh_gamification_snapshots()
// (supabase/migrations/20260827000003_add_gamification.sql) to match.

export type Level = {
  id: string;
  name: string;
  minScore: number;
  Icon: LucideIcon;
};

export const LEVELS: readonly Level[] = [
  { id: "explorer", name: "Explorer", minScore: 0, Icon: TelescopeIcon },
  { id: "practitioner", name: "Practitioner", minScore: 120, Icon: WrenchIcon },
  { id: "builder", name: "Builder", minScore: 320, Icon: BicepsFlexedIcon },
  { id: "champion", name: "Champion", minScore: 600, Icon: RocketIcon },
] as const;

export function computeLevel(lifetimeScore: number): Level {
  let level = LEVELS[0];
  for (const candidate of LEVELS) {
    if (lifetimeScore >= candidate.minScore) level = candidate;
  }
  return level;
}

export type LevelProgress = {
  level: Level;
  nextLevel: Level | null;
  /** 0..1 progress from the current level's floor to the next level's floor. */
  progress: number;
  pointsToNext: number;
};

export function levelProgress(lifetimeScore: number): LevelProgress {
  const level = computeLevel(lifetimeScore);
  const index = LEVELS.findIndex((l) => l.id === level.id);
  const nextLevel = index < LEVELS.length - 1 ? LEVELS[index + 1] : null;
  if (!nextLevel) {
    return { level, nextLevel: null, progress: 1, pointsToNext: 0 };
  }
  const span = nextLevel.minScore - level.minScore;
  const progress = Math.min(1, Math.max(0, (lifetimeScore - level.minScore) / span));
  return {
    level,
    nextLevel,
    progress,
    pointsToNext: Math.max(0, nextLevel.minScore - lifetimeScore),
  };
}

// ── Badges ───────────────────────────────────────────────────────────────────
// Computed on the fly from GamificationStats — no persistence. `howToUnlock`
// doubles as the "how to improve" surface on unearned tiles.

export type BadgeProgress = { current: number; target: number };

export type BadgeDef = {
  id: string;
  name: string;
  description: string;
  howToUnlock: string;
  Icon: LucideIcon;
  progress: (stats: GamificationStats) => BadgeProgress;
};

const bool = (value: boolean): BadgeProgress => ({ current: value ? 1 : 0, target: 1 });

export const BADGES: readonly BadgeDef[] = [
  {
    id: "first-chat",
    name: "First Words",
    description: "Sent your first message.",
    howToUnlock: "Start a chat and send a message.",
    Icon: MessageCircleIcon,
    progress: (s) => ({ current: Math.min(s.total_messages, 1), target: 1 }),
  },
  {
    id: "conversationalist",
    name: "Conversationalist",
    description: "Sent 10 messages.",
    howToUnlock: "Send 10 messages in any chats.",
    Icon: MessagesSquareIcon,
    progress: (s) => ({ current: s.total_messages, target: 10 }),
  },
  {
    id: "centurion",
    name: "Centurion",
    description: "Sent 100 messages.",
    howToUnlock: "Send 100 messages in any chats.",
    Icon: MedalIcon,
    progress: (s) => ({ current: s.total_messages, target: 100 }),
  },
  {
    id: "agent-architect",
    name: "Agent Architect",
    description: "Created your first agent.",
    howToUnlock: "Build an agent from the Agents page.",
    Icon: BotIcon,
    progress: (s) => ({ current: Math.min(s.agents_created, 1), target: 1 }),
  },
  {
    id: "web-explorer",
    name: "Web Explorer",
    description: "Used web search in a chat.",
    howToUnlock: "Ask a question that needs fresh information with web search on.",
    Icon: GlobeIcon,
    progress: (s) => bool(s.used_web_search),
  },
  {
    id: "code-runner",
    name: "Code Runner",
    description: "Ran code with code execution.",
    howToUnlock: "Ask for a calculation, chart, or file analysis with code execution on.",
    Icon: TerminalIcon,
    progress: (s) => bool(s.used_code_execution),
  },
  {
    id: "librarian",
    name: "Librarian",
    description: "Queried a knowledge base.",
    howToUnlock: "Chat with an agent that has a knowledge base attached.",
    Icon: LibraryIcon,
    progress: (s) => bool(s.used_knowledge_base),
  },
  {
    id: "meeting-captain",
    name: "Meeting Captain",
    description: "Uploaded your first meeting.",
    howToUnlock: "Record or upload a meeting to get minutes.",
    Icon: AudioLinesIcon,
    progress: (s) => ({ current: Math.min(s.meetings_uploaded, 1), target: 1 }),
  },
  {
    id: "artificer",
    name: "Artificer",
    description: "Created your first artifact.",
    howToUnlock: "Ask for a document or page and save it as an artifact.",
    Icon: SparklesIcon,
    progress: (s) => ({ current: Math.min(s.artifacts_created, 1), target: 1 }),
  },
  {
    id: "prompt-keeper",
    name: "Prompt Keeper",
    description: "Saved your first prompt.",
    howToUnlock: "Save a reusable prompt from the Prompts page.",
    Icon: BookmarkIcon,
    progress: (s) => ({ current: Math.min(s.prompts_saved, 1), target: 1 }),
  },
  {
    id: "model-curious",
    name: "Model Curious",
    description: "Chatted with 2 different models.",
    howToUnlock: "Try a different model from the model picker.",
    Icon: ShuffleIcon,
    progress: (s) => ({ current: s.distinct_models_used, target: 2 }),
  },
  {
    id: "model-connoisseur",
    name: "Model Connoisseur",
    description: "Chatted with 3 different models.",
    howToUnlock: "Use three different models across your chats.",
    Icon: LayersIcon,
    progress: (s) => ({ current: s.distinct_models_used, target: 3 }),
  },
  {
    id: "weekly-regular",
    name: "Weekly Regular",
    description: "Active 3 days in one week.",
    howToUnlock: "Use Woods AI on 3 different days in a week.",
    Icon: CalendarCheckIcon,
    progress: (s) => ({ current: s.max_active_days_in_week, target: 3 }),
  },
  {
    id: "four-week-streak",
    name: "On a Roll",
    description: "Active 2+ days a week, 4 weeks straight.",
    howToUnlock: "Stay active at least 2 days a week for 4 weeks in a row.",
    Icon: FlameIcon,
    progress: (s) => ({ current: s.best_week_streak, target: 4 }),
  },
  {
    id: "project-pioneer",
    name: "Project Pioneer",
    description: "Chatted inside a project.",
    howToUnlock: "Open a project and start a chat in it.",
    Icon: FolderKanbanIcon,
    progress: (s) => ({ current: Math.min(s.total_project_messages, 1), target: 1 }),
  },
  {
    id: "workflow-operator",
    name: "Workflow Operator",
    description: "Ran your first workflow.",
    howToUnlock: "Run a managed agent from the Workflows page.",
    Icon: WorkflowIcon,
    progress: (s) => ({ current: Math.min(s.workflow_sessions, 1), target: 1 }),
  },
] as const;

export function isBadgeEarned(badge: BadgeDef, stats: GamificationStats): boolean {
  const { current, target } = badge.progress(stats);
  return current >= target;
}

export function countEarnedBadges(stats: GamificationStats): number {
  return BADGES.filter((badge) => isBadgeEarned(badge, stats)).length;
}

// ── Nudges ───────────────────────────────────────────────────────────────────

export type Nudge = {
  id: string;
  title: string;
  description: string;
  Icon: LucideIcon;
};

export function deriveNudges(stats: GamificationStats): Nudge[] {
  const nudges: Nudge[] = [];

  // Nearest unearned badge: highest progress ratio first, so the suggestion
  // is always the smallest step with a visible payoff.
  const nearest = BADGES
    .map((badge) => ({ badge, ...badge.progress(stats) }))
    .filter(({ current, target }) => current < target)
    .sort((a, b) => b.current / b.target - a.current / a.target)[0];
  if (nearest) {
    nudges.push({
      id: `badge-${nearest.badge.id}`,
      title: `Unlock ${nearest.badge.name}`,
      description: nearest.badge.howToUnlock,
      Icon: nearest.badge.Icon,
    });
  }

  const unusedTool = !stats.used_web_search
    ? {
        id: "try-web-search",
        badgeId: "web-explorer",
        title: "Try web search",
        description: "Ask about something recent — Woods AI can search the web for you.",
        Icon: GlobeIcon,
      }
    : !stats.used_code_execution
      ? {
          id: "try-code-execution",
          badgeId: "code-runner",
          title: "Try code execution",
          description: "Hand over a spreadsheet or a calculation and let AI run the numbers.",
          Icon: TerminalIcon,
        }
      : !stats.used_knowledge_base
        ? {
            id: "try-knowledge-base",
            badgeId: "librarian",
            title: "Try a knowledge base",
            description: "Chat with an agent that knows your documents.",
            Icon: LibraryIcon,
          }
        : null;
  if (unusedTool && nudges[0]?.id !== `badge-${unusedTool.badgeId}`) {
    const { badgeId: _badgeId, ...nudge } = unusedTool;
    nudges.push(nudge);
  }

  const { nextLevel, pointsToNext } = levelProgress(stats.lifetime_score);
  if (nextLevel) {
    nudges.push({
      id: "next-level",
      title: `${pointsToNext} points to ${nextLevel.name}`,
      description:
        "Points come from trying new things and showing up regularly — not volume.",
      Icon: nextLevel.Icon,
    });
  }

  return nudges.slice(0, 3);
}
